import * as THREE from 'three';
import { clamp, smoothstep } from '@/core/rng';

/**
 * Rain, seen from outside it — the shafts hanging under a front three valleys
 * away.
 *
 * `render/rain.ts` draws rain as a shell of streaks riding the camera, which
 * is the right model for rain you are standing in: a raindrop is not a thing,
 * it is the state of the air, and the air two metres from your eye is the only
 * air whose drops resolve. But it means rain exists *only* where the presence
 * is, and `tools/season.ts` measured what that costs:
 *
 *     rain falling on you            12.9% of a real hour
 *     rain falling SOMEWHERE IN VIEW 80.3% of a real hour
 *
 * Four fifths of the time the sky is visibly doing something and the renderer
 * was drawing none of it. The front field is a ~14 km feature drifting
 * downwind — one crosses the island in a world afternoon, a few real minutes at
 * the shipped clock — so this is also the fastest-changing thing a player can
 * look at, and the only part of it that reached the frame was an 18% dimming of
 * the hemisphere light.
 *
 * ## What this is, and what it deliberately is not
 *
 * It is a small pool of vertical billboards, each hung under the cloud base
 * over a patch of ground the **same `weatherAt` the simulation reads** says is
 * wet. It is not a particle system, not volumetric, and not a second rain
 * model: nothing here decides whether it is raining, it only draws where the
 * front already says it is. Law 1 is safe because this reads the field and
 * never writes it, and law 10 gains something real — a bolt now falls out of a
 * sky a witness could have watched arrive.
 *
 * Two things make it read as weather rather than as geometry:
 *
 *  · **It is the haze's own colour**, taken from `scene.fog` each frame, only
 *    barely darkened and cooled. A rain shaft is scattered sky light; painting
 *    it grey against a golden evening would make it a prop.
 *  · **It frays at the bottom.** Real distant rain is dense under the cloud and
 *    dissolves before the ground as often as not, which is virga, and it is the
 *    single cue that stops a vertical quad reading as a wall.
 *
 * And one thing keeps it from fighting the shell: a curtain within `NEAR_FADE`
 * of the eye fades out completely, so where you are standing in the rain the
 * streaks own it and nothing is drawn twice.
 */

export interface CurtainSample {
  /** 0..1 — the front's rain at this point, the same number the sim reads. */
  rain: number;
  /** 0..1 — its violent core, which hangs lower and darker. */
  storm: number;
}

export interface Curtains {
  group: THREE.Group;
  /**
   * Per frame. `sample` is the weather field, `groundAt` the surface height,
   * both called at most `CELLS`² times and only every `REFRESH` seconds.
   */
  update(
    tSec: number, eye: THREE.Vector3, haze: THREE.Color,
    sample: (x: number, z: number) => CurtainSample,
    groundAt: (x: number, z: number) => number,
  ): void;
  /** How many shafts are being drawn — for the render panel and for probes. */
  readonly drawn: number;
  dispose(): void;
}

/** The pool. Measured usage is ~9 of these at once; the rest is headroom. */
const POOL = 28;
/** The lattice the field is sampled on, and how far out it reaches. */
const CELLS = 9, SPAN = 15000;
/** Below this much rain a patch is not worth a shaft. */
const MIN_RAIN = 0.26;
/**
 * Cloud base, and how far a storm core hangs below it. Metres.
 *
 * 780 was the cumulus layer's lower edge and it was too low to read: a shaft
 * that short, seen from kilometres away, is a horizontal smear rather than a
 * fall. Rain hangs from higher than the fair-weather puffs do.
 */
const BASE = 1250, STORM_DROP = 260;
/** Inside this distance the camera-riding streaks own the rain instead. */
const NEAR_FADE = 900;
/** The field is resampled this often, in seconds — a front moves slowly. */
const REFRESH = 0.45;

/**
 * One shaft's texture: dense under the cloud, frayed at the foot, soft-edged.
 *
 * Built once and shared. The vertical ramp is what does the work — a flat quad
 * with a hard bottom edge reads as a pane of glass standing in the valley, and
 * the fray is the difference between that and weather.
 */
function shaftTexture(): THREE.CanvasTexture {
  const w = 64, h = 128;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, w, h);

  // a few soft vertical bands, so the shaft has internal structure and does
  // not read as one airbrushed smear
  let seed = 0x51a7;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
  for (let i = 0; i < 22; i++) {
    const x = rnd() * w;
    const bw = 1.5 + rnd() * 5;
    const top = rnd() * 0.12 * h;
    // each band ends somewhere different — that IS the fray
    const bot = h * (0.55 + rnd() * 0.45);
    const grad = g.createLinearGradient(0, top, 0, bot);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.18, `rgba(255,255,255,${0.30 + rnd() * 0.28})`);
    grad.addColorStop(0.72, `rgba(255,255,255,${0.10 + rnd() * 0.16})`);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(x - bw / 2, top, bw, bot - top);
  }
  // and one broad wash binding them into a single mass
  const wash = g.createLinearGradient(0, 0, 0, h);
  wash.addColorStop(0, 'rgba(255,255,255,0)');
  wash.addColorStop(0.16, 'rgba(255,255,255,0.34)');
  wash.addColorStop(0.62, 'rgba(255,255,255,0.20)');
  wash.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = wash;
  g.fillRect(0, 0, w, h);

  // feather the left and right edges, or the shaft has two vertical seams
  const px = g.getImageData(0, 0, w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const e = Math.min(x, w - 1 - x) / (w * 0.22);
    px.data[(y * w + x) * 4 + 3] *= clamp(e, 0, 1);
  }
  g.putImageData(px, 0, 0);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function createCurtains(): Curtains {
  const group = new THREE.Group();
  group.name = 'curtains';
  const tex = shaftTexture();
  const geo = new THREE.PlaneGeometry(1, 1);

  interface Shaft {
    mesh: THREE.Mesh;
    mat: THREE.MeshBasicMaterial;
    /** Where the field said it was raining, and how hard. */
    x: number; z: number; rain: number; storm: number; ground: number;
    live: boolean;
  }
  const shafts: Shaft[] = [];
  for (let i = 0; i < POOL; i++) {
    // Basic, not Lambert: a rain shaft is scattered light rather than a lit
    // surface, and a lit one goes black on the shadowed side of the world
    // exactly when the sky behind it is brightest.
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, opacity: 0,
      side: THREE.DoubleSide, fog: true,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = false;
    mesh.renderOrder = 3;       // behind the reticle, in front of the sky
    mesh.frustumCulled = false; // billboards are re-aimed every frame
    group.add(mesh);
    shafts.push({ mesh, mat, x: 0, z: 0, rain: 0, storm: 0, ground: 0, live: false });
  }

  let nextAt = -1;
  let drawn = 0;
  const tmp = new THREE.Vector3();

  return {
    group,
    get drawn() { return drawn; },

    update(tSec, eye, haze, sample, groundAt) {
      // 1. Re-read the field, occasionally. A 14 km front crossing at 3 m/s
      //    moves 1.4 m in the time between frames; resampling 81 cells every
      //    frame would be paying a lot for that.
      if (tSec >= nextAt) {
        nextAt = tSec + REFRESH;
        // strongest-and-nearest first, so a small pool spends itself on the
        // shafts that will actually read
        const found: Array<{ x: number; z: number; rain: number; storm: number; score: number }> = [];
        for (let j = 0; j < CELLS; j++) for (let i = 0; i < CELLS; i++) {
          const x = eye.x + (i / (CELLS - 1) - 0.5) * SPAN;
          const z = eye.z + (j / (CELLS - 1) - 0.5) * SPAN;
          const w = sample(x, z);
          if (w.rain < MIN_RAIN) continue;
          const d = Math.hypot(x - eye.x, z - eye.z);
          if (d < NEAR_FADE * 0.5) continue;
          // a hard shaft close by is worth more of the budget than a faint
          // one at the edge of sight
          found.push({ x, z, rain: w.rain, storm: w.storm,
                       score: w.rain * (1 + w.storm) / (1 + d / SPAN) });
        }
        found.sort((a, b) => b.score - a.score);
        for (let k = 0; k < shafts.length; k++) {
          const s = shafts[k], f = found[k];
          if (!f) { s.live = false; continue; }
          // jitter each shaft off the lattice by a stable hash of its cell, or
          // the curtains stand in visible rows — the same mistake the far
          // canopy's NearestFilter hole made on 256 m cell edges
          const hx = Math.sin(f.x * 0.013 + f.z * 0.007) * 43758.5453;
          const hz = Math.sin(f.x * 0.009 - f.z * 0.017) * 43758.5453;
          const jx = (hx - Math.floor(hx) - 0.5) * (SPAN / CELLS) * 0.8;
          const jz = (hz - Math.floor(hz) - 0.5) * (SPAN / CELLS) * 0.8;
          s.x = f.x + jx; s.z = f.z + jz;
          s.rain = f.rain; s.storm = f.storm;
          s.ground = Math.max(0, groundAt(s.x, s.z));
          s.live = true;
        }
      }

      // 2. Aim and tint them. Every frame, because both depend on the eye.
      drawn = 0;
      for (const s of shafts) {
        if (!s.live) { s.mesh.visible = false; continue; }
        const dx = s.x - eye.x, dz = s.z - eye.z;
        const d = Math.hypot(dx, dz);

        // near the eye the streak shell owns the rain; far away the aerial
        // perspective has already eaten the shaft, so both ends fade
        const near = smoothstep(NEAR_FADE * 0.5, NEAR_FADE * 1.8, d);
        const far = 1 - smoothstep(SPAN * 0.55, SPAN * 0.85, d);
        const a = clamp(s.rain, 0, 1) * near * far;
        if (a < 0.02) { s.mesh.visible = false; continue; }

        // a storm core hangs lower and heavier than ordinary rain
        const base = BASE - s.storm * STORM_DROP;
        const top = s.ground + base;
        // …and the foot of the shaft stops short of the ground when the rain
        // is light: that is virga, and it is why light rain reads as a smudge
        // in the air rather than as a curtain touching down
        const foot = s.ground + (1 - smoothstep(0.3, 0.8, s.rain)) * base * 0.34;
        const height = Math.max(60, top - foot);
        // Width is the one number two frames in a row got wrong in opposite
        // directions. At 0.55·d the shafts read as separate pale panels — the
        // eye counts them, which is the tell that they are objects. At 0.95·d
        // they overlapped, but each was 5.7 km wide against 780 m of height:
        // SEVEN TO ONE horizontal, so a "shaft" was a smear lying on its side.
        // A fall has to be taller than it is wide, or at worst comparable. So
        // the width is bounded in metres rather than scaled off distance, and
        // the merging comes from having many of them rather than from each
        // being enormous.
        const width = clamp(d * 0.30, 1100, 2800);

        s.mesh.position.set(s.x, (top + foot) / 2, s.z);
        s.mesh.scale.set(width, height, 1);
        // billboard about Y only — rain falls down, whatever the eye is doing
        tmp.set(eye.x - s.x, 0, eye.z - s.z);
        s.mesh.rotation.set(0, Math.atan2(tmp.x, tmp.z), 0);

        // The haze's own colour, cooled and DARKENED — and the amount matters
        // more than it looks. At 0.90 of the haze a shaft is very nearly the
        // colour of the sky behind it, so it read only where it happened to
        // cross terrain and vanished completely against the sky above the
        // horizon, which is exactly backwards: rain against a bright sky is
        // the one place it is unmistakable. At 0.78 it dims a bright sky the
        // way real rain does and still lightens a dark hillside, because the
        // hillside is darker than it is.
        s.mat.color.copy(haze).multiplyScalar(0.78 - s.storm * 0.18);
        s.mat.color.b = Math.min(1, s.mat.color.b * (1.05 + s.storm * 0.06));
        // 0.30 read as a solid pale slab against a dark hillside — closer to
        // a shaft of light than to rain. Distant rain is a VEIL: it lightens
        // what is behind it and is never brighter than the sky it hangs from.
        s.mat.opacity = a * (0.24 + s.storm * 0.18);
        s.mesh.visible = true;
        drawn++;
      }
    },

    dispose() {
      for (const s of shafts) s.mat.dispose();
      geo.dispose();
      tex.dispose();
    },
  };
}
